import * as React from 'react';
import { Portrait } from './portrait';

const PAPER = '#FEF3EF';
const TAPE = 'rgba(248, 215, 221, 0.8)';
const RED = '#D41920';

/**
 * Styles for the clickable plate: a slow breathing pulse (scale + red glow
 * that follows the cut-out's shape) while her story is unseen, a lift on
 * hover/focus so it reads as a button, and the "play" badge. Reduced motion:
 * no pulse, the badge alone says "tap me".
 */
const PLATE_STYLES = `
@keyframes portraitPlatePulse {
  0%, 100% { transform: scale(1); filter: drop-shadow(0 0 0 rgba(212, 25, 32, 0)); }
  50% { transform: scale(1.035); filter: drop-shadow(0 0 18px rgba(212, 25, 32, 0.55)); }
}
@keyframes portraitPlateBadge {
  0%, 100% { transform: scale(1); }
  50% { transform: scale(1.14); }
}
.portrait-plate-btn { transition: transform 0.2s ease, filter 0.2s ease; }
.portrait-plate-btn[data-unseen="true"] { animation: portraitPlatePulse 2.6s ease-in-out infinite; }
.portrait-plate-btn[data-unseen="true"] .portrait-plate-badge { animation: portraitPlateBadge 2.6s ease-in-out infinite; }
.portrait-plate-btn:hover, .portrait-plate-btn:focus-visible {
  animation: none;
  transform: scale(1.05);
  filter: drop-shadow(0 10px 22px rgba(212, 25, 32, 0.45));
}
.portrait-plate-btn:hover .portrait-plate-badge, .portrait-plate-btn:focus-visible .portrait-plate-badge { animation: none; transform: scale(1.14); }
.portrait-plate-btn:active { transform: scale(0.99); }
.portrait-plate-btn:focus-visible { outline: 3px solid #F2A0C4; outline-offset: 4px; }
@media (prefers-reduced-motion: reduce) {
  .portrait-plate-btn, .portrait-plate-btn .portrait-plate-badge { animation: none !important; }
  .portrait-plate-btn:hover, .portrait-plate-btn:focus-visible, .portrait-plate-btn:active { transform: none; }
}
`;

/**
 * PortraitPlate — the woman's collage cut-out, big: pasted on a torn cream
 * paper plate with two tape strips and (optionally) a red brush stroke behind,
 * the way the landing page shows her. Used by the arrival banner and by the
 * expanded poster. Without a portrait URL it falls back to the stencil disc.
 *
 * Pass `onOpen` to make the plate a button (her story opens from it).
 */
export function PortraitPlate({
  url,
  name,
  initial,
  size,
  tilt = 3,
  brush = true,
  brushColor = '#E84B5A',
  fallbackColor = '#D41920',
  onOpen,
  openLabel,
  storySeen = true,
  style,
}: {
  url: string | null | undefined;
  name?: string | null;
  initial: string;
  /** plate width in px (height follows, slightly taller) */
  size: number;
  /** plate rotation, degrees */
  tilt?: number;
  /** paint a red brush blob behind the plate */
  brush?: boolean;
  brushColor?: string;
  /** stencil colour when there is no portrait */
  fallbackColor?: string;
  onOpen?: () => void;
  /** accessible name of the button when `onOpen` is set */
  openLabel?: string;
  /**
   * With `onOpen`: false while her story is still unseen — the plate pulses
   * and the badge is red, so the first meeting is hard to miss; true once
   * seen — quiet badge, no pulse.
   */
  storySeen?: boolean;
  style?: React.CSSProperties;
}) {
  const height = Math.round(size * 1.06);
  const tapeW = Math.round(size * 0.28);
  const tapeH = Math.max(14, Math.round(size * 0.065));

  const plate = (
    <div
      aria-hidden={onOpen ? undefined : true}
      style={{
        position: 'relative',
        width: size,
        height,
        flexShrink: 0,
        ...style,
      }}
    >
      {brush && (
        <div
          aria-hidden
          style={{
            position: 'absolute',
            left: '-4%',
            top: '6%',
            width: '108%',
            height: '90%',
            background: `radial-gradient(ellipse at 50% 50%, ${brushColor} 0%, ${brushColor} 55%, transparent 72%)`,
            transform: 'rotate(-8deg)',
            opacity: 0.9,
          }}
        />
      )}
      <div
        style={{
          position: 'absolute',
          left: '5%',
          top: 0,
          width: '90%',
          height: '100%',
          background: PAPER,
          borderRadius: '46% 54% 50% 50% / 52% 48% 56% 44%',
          boxShadow: '0 16px 36px rgba(26, 16, 24, 0.35)',
          display: 'flex',
          alignItems: 'flex-end',
          justifyContent: 'center',
          overflow: 'hidden',
          transform: `rotate(${tilt}deg)`,
        }}
      >
        {url ? (
          <img
            src={url}
            alt={onOpen ? '' : (name ?? '')}
            loading="lazy"
            style={{
              width: '96%',
              height: '96%',
              objectFit: 'contain',
              objectPosition: 'center bottom',
              display: 'block',
              transform: `rotate(${-tilt}deg)`,
            }}
          />
        ) : (
          <Portrait
            url={null}
            initial={initial}
            name={name}
            color={fallbackColor}
            size={Math.round(size * 0.62)}
            style={{ margin: 'auto' }}
          />
        )}
      </div>
      {/* tape strips */}
      <div
        aria-hidden
        style={{
          position: 'absolute',
          left: '10%',
          top: -Math.round(tapeH * 0.3),
          width: tapeW,
          height: tapeH,
          background: TAPE,
          transform: 'rotate(-14deg)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
        }}
      />
      <div
        aria-hidden
        style={{
          position: 'absolute',
          right: '4%',
          bottom: '8%',
          width: tapeW,
          height: tapeH,
          background: TAPE,
          transform: 'rotate(16deg)',
          boxShadow: '0 1px 2px rgba(0,0,0,0.12)',
        }}
      />
    </div>
  );

  if (!onOpen) return plate;

  const badgeSize = Math.max(30, Math.round(size * 0.15));
  return (
    <button
      type="button"
      className="portrait-plate-btn"
      data-testid="portrait-plate-btn"
      data-unseen={storySeen ? 'false' : 'true'}
      aria-label={openLabel ?? name ?? undefined}
      title={openLabel ?? undefined}
      onClick={(e) => {
        e.stopPropagation();
        onOpen();
      }}
      onKeyDown={(e) => e.stopPropagation()}
      style={{
        appearance: 'none',
        background: 'transparent',
        border: 0,
        padding: 0,
        margin: 0,
        cursor: 'pointer',
        display: 'block',
        lineHeight: 0,
        borderRadius: '50%',
        position: 'relative',
      }}
    >
      <style dangerouslySetInnerHTML={{ __html: PLATE_STYLES }} />
      {plate}
      {/* "play" badge: there is a story behind this picture */}
      <span
        aria-hidden
        className="portrait-plate-badge"
        style={{
          position: 'absolute',
          right: '6%',
          bottom: '9%',
          width: badgeSize,
          height: badgeSize,
          borderRadius: '50%',
          background: storySeen ? '#8A6F69' : RED,
          border: '3px solid #FFFFFF',
          boxShadow: '0 4px 12px rgba(26, 16, 24, 0.35)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          boxSizing: 'border-box',
        }}
      >
        <svg
          width={Math.round(badgeSize * 0.42)}
          height={Math.round(badgeSize * 0.42)}
          viewBox="0 0 24 24"
          fill="#FFFFFF"
          style={{ marginLeft: '8%' }}
        >
          <path d="M6 4.5v15l13-7.5z" />
        </svg>
      </span>
    </button>
  );
}
